import { getVar } from "config";
import {
  createProvider,
  createSamlConfig,
  updateProvider,
  updateSamlConfig,
} from "services/apiService/apis";
import {
  SSO_CALLBACK_URL_DEV,
  SSO_CALLBACK_URL_PRODUCTION,
  SSO_ENTITY_ID_DEV,
  SSO_ENTITY_ID_PRODUCTION,
} from "./constants";
import { ISsoInfo } from "./types";

interface ISsoParamsForEnvironmentData {
  callbackURL: string;
  serviceProviderEntityID: string;
}

const getSsoParamsForEnvironment = (): ISsoParamsForEnvironmentData => {
  const gcloudProjectName = getVar("REACT_APP_GCLOUD_PROJECT_NAME");
  const serviceProviderEntityID =
    gcloudProjectName === "remo-conference-dev"
      ? SSO_ENTITY_ID_DEV
      : SSO_ENTITY_ID_PRODUCTION;
  const callbackURL =
    gcloudProjectName === "remo-conference-dev"
      ? SSO_CALLBACK_URL_DEV
      : SSO_CALLBACK_URL_PRODUCTION;

  return { callbackURL, serviceProviderEntityID };
};

const transformNameForProviderId = (displayName: string) =>
  displayName.replace(/\s+/g, "-").toLowerCase();

const createProviderAsync = async (configData: ISsoInfo) => {
  const { callbackURL, serviceProviderEntityID } = getSsoParamsForEnvironment();

  const samlProviderRes = await createProvider({
    enabled: configData.enabled || false,
    ssoURL: configData.authenticationEndpoint,
    idpEntityId: configData.identityProviderEntityID,
    x509Certificates: [configData.publicCertificate || ""],
    providerId: transformNameForProviderId(`saml.${configData.displayName}`),
    callbackURL,
    displayName: configData.displayName,
    rpEntityId: serviceProviderEntityID,
  });

  return samlProviderRes;
};

const updateProviderAsync = async (configData: ISsoInfo) => {
  const samlProviderRes = await updateProvider({
    enabled: configData.enabled || false,
    ssoURL: configData.authenticationEndpoint,
    providerId: configData.providerId,
    idpEntityId: configData.identityProviderEntityID,
    displayName: configData.displayName,
    x509Certificates: [configData.publicCertificate || ""],
  });

  return samlProviderRes;
};

const createSamlConfigAsync = async (
  companyId: string,
  configData: ISsoInfo,
) => {
  const createCompanySettingsProps = {
    displayName: configData.displayName,
    providerId: transformNameForProviderId(`saml.${configData.displayName}`),
    enabled: configData.enabled,
  };

  const samlProviderRes = await createSamlConfig(
    companyId,
    createCompanySettingsProps,
  );

  return samlProviderRes;
};

const updateSamlConfigAsync = async (
  companyId: string,
  configData: ISsoInfo,
) => {
  const updateCompanySettingsProps = {
    displayName: configData.displayName,
    providerId: configData.providerId,
    enabled: configData.enabled,
  };

  const samlProviderRes = await updateSamlConfig(
    companyId,
    updateCompanySettingsProps,
  );

  return samlProviderRes;
};

export {
  createProviderAsync,
  updateProviderAsync,
  createSamlConfigAsync,
  updateSamlConfigAsync,
};
